import { v4 as uuidv4 } from "uuid"
import { measureParentSpaces } from "../../../../helpers/domSpace.helpers"
import { defaultGroupWrapper } from "../../../../data/defaults/objects.types.defaults"
import {
    ScaleBox,
    mapBoxThroughResize,
    scaleObjectsInBounds,
} from "../../../../helpers/multiResize.helpers"
import { createObject } from "../../../../helpers/object.helpers"
import {
    flattenTimelineObjectIds,
    flattenVisibleObjectIds,
} from "../../../../helpers/objectsTree.helpers"
import { selectUndoable } from "../../../../helpers/selector.helpers"
import {
    ObjectsById,
    computeWrapperBounds,
    domObjectSize,
    getDescendantsOfSelected,
    multiSelectionWrapper,
    singleSelectionWrapper,
} from "../../../../helpers/wrapper.helpers"
import { AppThunkT } from "../../../store"
import { setSelectedAnimationsForObjectAction } from "../../activeAnimation.slice"
import { clearAllSelectedObjectsAction, setSelectedObjectIdsAction } from "../../activeObject.slice"
import {
    delMultiObjectsAction,
    duplicateObjects,
    groupObjectsActionAsync,
    selectObjectsByIds,
    selectSortedRootObjects,
    updateObjectsPositionAction,
} from "../../objects.slice"
import { findCommonParentId } from "./groupObjectsBaseAction"

/**
 * Duplicate / delete / group of the current selection, moved out of
 * `useMultiselectObjectAction`.
 *
 * That hook is instantiated in every CanvasItem and every ObjectsTreeNodeMenu — several
 * hundred times on a large graphic — and returns nothing but these three handlers. Keeping
 * the data in the hook meant subscribing to the selection array and running two
 * DOM-measuring `computeWrapperBounds` calls per instance on every render. As thunks the
 * same data is read from `getState()` only when the user triggers the action.
 */

export const duplicateSelectedObjectsAction = (): AppThunkT => (dispatch, getState) => {
    const state = getState()
    dispatch(
        duplicateObjects(
            selectObjectsByIds(selectUndoable(state).objects, state.activeObject.selected)
        )
    )
}

export const deleteSelectedObjectsAction = (): AppThunkT => (dispatch, getState) => {
    const state = getState()
    dispatch(
        delMultiObjectsAction(
            selectObjectsByIds(selectUndoable(state).objects, state.activeObject.selected)
        )
    )
    dispatch(clearAllSelectedObjectsAction())
}

export const groupSelectedObjectsAction = (): AppThunkT => (dispatch, getState) => {
    const state = getState()
    const objects = selectUndoable(state).objects
    const objectsById = objects.value as ObjectsById
    const selectedObjectIds = state.activeObject.selected

    if (selectedObjectIds.length === 0) return

    const commonParentId = findCommonParentId(Object.values(objectsById), selectedObjectIds)
    const isSingle = selectedObjectIds.length === 1

    const { wrapperObjectsStyles } = isSingle
        ? singleSelectionWrapper(selectedObjectIds[0], objectsById)
        : { wrapperObjectsStyles: null }
    const multi = isSingle ? null : multiSelectionWrapper(selectedObjectIds, objectsById)

    const bounds = isSingle ? wrapperObjectsStyles! : multi!.wrapperObject
    const { width, height, top, left } = bounds

    const groupObject = createObject(
        Object.values(objectsById),
        defaultGroupWrapper({ width, height, top, left }),
        commonParentId,
        "i" + uuidv4(),
        true
    )

    dispatch(
        groupObjectsActionAsync(
            groupObject,
            selectObjectsByIds(objects, selectedObjectIds),
            bounds,
            isSingle ? [wrapperObjectsStyles!] : multi!.allObjectsInWrapper
        )
    )
}

/**
 * Resize the whole multiselection to `after`.
 *
 * The box it is resized FROM is recomputed here rather than passed in, so the ratios can
 * never be derived from a bounding box that has drifted from the one the objects are
 * actually in — `computeWrapperBounds` is also what draws the selection frame.
 *
 * Every affected object lands in a single `updateObjectsPositionAction`, so the whole
 * resize is one undo step instead of one per object.
 *
 * @param after the selection's bounding box after the drag, in absolute canvas coordinates
 * @param drawnBefore the box the selection was DRAWN in when the drag started — measured off
 * the screen, so it includes rotations and borders the styles know nothing about. The drag
 * is replayed from it onto the styles' own box (see `mapBoxThroughResize`).
 */
export const resizeSelectedObjectsAction =
    (after: ScaleBox, drawnBefore?: ScaleBox): AppThunkT =>
    (dispatch, getState) => {
        const state = getState()
        const objectsById = selectUndoable(state).objects.value as ObjectsById
        const selectedObjectIds = state.activeObject.selected

        if (selectedObjectIds.length < 2) return

        const bounds = computeWrapperBounds(selectedObjectIds, objectsById, domObjectSize)
        const before: ScaleBox = {
            top: bounds.origin.top,
            left: bounds.origin.left,
            width: bounds.width,
            height: bounds.height,
        }

        const target = drawnBefore ? mapBoxThroughResize(before, drawnBefore, after) : after
        // members in a parent that stays as it is are resized in the drawn geometry
        const canvasElement = document.getElementById("canvas")
        const drawn =
            drawnBefore && canvasElement
                ? {
                      before: drawnBefore,
                      after,
                      parentSpaces: measureParentSpaces(
                          bounds.objectsAbsolute.map((entry) => entry.objectId),
                          (id) => objectsById[id]?.parentId,
                          (id) => document.getElementById(id),
                          canvasElement
                      ),
                  }
                : undefined
        const updates = scaleObjectsInBounds(
            bounds.objectsAbsolute,
            before,
            target,
            objectsById,
            drawn
        )
        if (updates.length > 0) dispatch(updateObjectsPositionAction(updates))
    }

/**
 * Which list was shift-clicked. The two show different rows, so a range means a different
 * set of objects in each — see the note on `flattenTimelineObjectIds`.
 */
export type SelectionListScope = "objectsTree" | "timeline"

/**
 * Shift-click in a list of objects: select every row between the last selected one and the
 * clicked one, the way shift-click already works on animation tweens.
 *
 * The anchor is the LAST id in the current selection, so repeated shift-clicks walk the
 * range outwards from where the user last landed. With nothing selected there is nothing to
 * span, so it degrades to selecting the clicked object alone.
 *
 * @param targetId the row that was shift-clicked
 * @param scope the list it was clicked in, which decides what "between" spans
 */
export const selectObjectsRangeAction =
    (targetId: string, scope: SelectionListScope): AppThunkT =>
    (dispatch, getState) => {
        const state = getState()
        const objects = selectUndoable(state).objects
        const objectsById = objects.value as ObjectsById
        const selected = state.activeObject.selected

        const roots = selectSortedRootObjects(objects)
        const visibleOrder =
            scope === "timeline"
                ? flattenTimelineObjectIds(roots, objectsById)
                : flattenVisibleObjectIds(roots, objectsById)
        const targetIndex = visibleOrder.indexOf(targetId)
        if (targetIndex === -1) return

        // the anchor can be gone or folded out of sight — then the range is just the click
        const anchorIndex =
            selected.length > 0 ? visibleOrder.indexOf(selected[selected.length - 1]) : -1
        const from = anchorIndex === -1 ? targetIndex : Math.min(anchorIndex, targetIndex)
        const to = anchorIndex === -1 ? targetIndex : Math.max(anchorIndex, targetIndex)

        const range = visibleOrder.slice(from, to + 1)
        // a selected ancestor already represents everything under it
        const coveredByAnAncestor = new Set(getDescendantsOfSelected(range, objectsById))
        const objectIds = range.filter((id) => !coveredByAnAncestor.has(id))

        const animationsIds = objectIds.flatMap((id) =>
            Object.keys(objectsById[id]?.animations ?? {})
        )

        dispatch(setSelectedObjectIdsAction({ objectIds }))
        dispatch(setSelectedAnimationsForObjectAction({ animationsIds }))
    }
